import type {
  CaptainDefinition,
  CaptainId,
  FacilityDefinition,
  FacilityId,
  GameMode,
  ResourceWallet,
  RunModifiers,
  SaveData,
  StageDefinition,
} from './types';

export const CAPTAINS: Record<CaptainId, CaptainDefinition> = {
  vanguard: {
    id: 'vanguard',
    name: 'ミナ・クロウ',
    callsign: 'VANGUARD',
    role: '防御',
    description: '先頭で隊列を支える護衛指揮官。壊滅する被害を一度だけ食い止める。',
    accent: '#73f0ff',
    passive: '致命被害を一度だけ無効化し、兵士5人で復帰',
  },
  gunner: {
    id: 'gunner',
    name: 'レオ・ヴァンス',
    callsign: 'GUNNER',
    role: '攻撃',
    description: '同じ標的を撃ち続けるほど火力が上がる、突破力重視の射撃手。',
    accent: '#ffb65c',
    passive: '同一標的への継続射撃で最大50%ダメージ上昇',
  },
  technician: {
    id: 'technician',
    name: 'ユイ・セト',
    callsign: 'TECH',
    role: '支援',
    description: '旧世界のドローンを再起動できる技術者。支援獲得時の効果が高い。',
    accent: '#b894ff',
    passive: '支援ゲート通過時に追加ドローンを1機展開',
  },
};

export const FACILITIES: Record<FacilityId, FacilityDefinition> = {
  hq: { id: 'hq', name: 'オリオン司令部', shortName: '司令部', description: '基地全体の強化上限を引き上げる', resource: 'blueprints', icon: '⌂' },
  barracks: { id: 'barracks', name: 'レンジャー兵舎', shortName: '兵舎', description: '出撃時の開始兵士数 +3', resource: 'food', icon: '⬡' },
  workshop: { id: 'workshop', name: 'アーク工房', shortName: '工房', description: '兵士1人あたりの攻撃力 +18%', resource: 'metal', icon: '⚙' },
  range: { id: 'range', name: 'ホーク射撃場', shortName: '射撃場', description: '連射速度と射程を強化', resource: 'food', icon: '⌁' },
  comms: { id: 'comms', name: 'ビーコン通信所', shortName: '通信所', description: '支援ドローンの火力 +15%', resource: 'electronics', icon: '◉' },
};

const stageNames = [
  ['灰の入口', '失われた高速道路を奪還せよ', 'ケルベロス・ハウンド'],
  ['分岐点', '崩れた料金所を突破せよ', '鉄塊ブルート'],
  ['残響の谷', '救難信号の発信源へ向かえ', 'サイレン・マザー'],
  ['黄昏の橋', '落橋前に対岸へ渡れ', 'ブリッジ・イーター'],
  ['第一防衛線', '夜襲部隊を迎撃せよ', 'ナイト・コロッサス'],
  ['錆びた動脈', '旧工業地帯へ侵入せよ', 'スクラップ・タイタン'],
  ['炉心区画', '暴走炉を停止させろ', 'メルト・ガーディアン'],
  ['搬送路ゼロ', '補給車列の道を開け', 'クラッシャー09'],
  ['雨の製鉄所', '視界不良の工場を制圧せよ', 'スモッグ・ウィドウ'],
  ['第二防衛線', '地下門への侵攻を止めろ', 'バスティオン'],
  ['黒雲平原', '電磁嵐の中心を抜けろ', 'ストーム・レイダー'],
  ['沈黙のアンテナ', '長距離通信を復旧せよ', 'ジャマー・クイーン'],
  ['雷光回廊', '落雷地帯の避難民を救え', 'ボルト・ベヒモス'],
  ['夜明け前', '最後の補給線を守り抜け', 'ドレッド・キャリア'],
  ['ブレイクライン', '人類の未来へ道を拓け', 'オメガ・ハーベスター'],
] as const;

const chapterAccents = ['#55e6f2', '#ffb65c', '#b894ff'] as const;
const biomes = ['canyon', 'industrial', 'storm'] as const;

export const STAGES: StageDefinition[] = stageNames.map((entry, index) => {
  const chapter = Math.floor(index / 5) + 1;
  const order = (index % 5) + 1;
  return {
    id: index + 1,
    chapter,
    order,
    name: entry[0],
    subtitle: entry[1],
    bossName: entry[2],
    biome: biomes[chapter - 1],
    length: 440 + index * 15,
    speed: 6.2 + index * 0.06,
    enemyHp: 32 + index * 6,
    enemyDamage: 3 + Math.floor(index / 3),
    bossHp: 3400 + index * 650,
    bossDamage: 7 + Math.floor(index / 4),
    seed: 8101 + index * 977,
    accent: chapterAccents[chapter - 1],
  };
});

export const RESOURCE_LABELS: Record<keyof ResourceWallet, string> = {
  food: '食料',
  metal: '金属',
  electronics: '電子部品',
  blueprints: '設計図',
};

export function getStage(id: number): StageDefinition {
  return STAGES.find((stage) => stage.id === id) ?? STAGES[0];
}

export function getFacilityCost(id: FacilityId, currentLevel: number): Partial<ResourceWallet> {
  const definition = FACILITIES[id];
  const base = definition.resource === 'blueprints' ? 1 : 80;
  const amount = Math.ceil(base * Math.pow(1.65, currentLevel - 1));
  return { [definition.resource]: amount };
}

export function getRunModifiers(save: SaveData): RunModifiers {
  const barracks = save.facilities.barracks;
  const workshop = save.facilities.workshop;
  const range = save.facilities.range;
  const comms = save.facilities.comms;
  return {
    startTroops: 16 + (barracks - 1) * 3,
    damagePerUnit: 1 + (workshop - 1) * 0.18,
    fireRate: 4.5 + (range - 1) * 0.35,
    range: 34 + (range - 1) * 2,
    supportBonus: 1 + (comms - 1) * 0.15,
  };
}

export function getModeLabel(mode: GameMode): string {
  if (mode === 'hard') return 'HARD';
  if (mode === 'daily') return 'DAILY';
  return 'NORMAL';
}
